import PropTypes from 'prop-types';
import { clsx } from 'clsx';
import CrossIcon from './icons/cross.svg?react';
import PaperClipIcon from './icons/paperclip.svg?react';
import { IconButton } from './primitives/IconButton';

export function AttachmentChip({
  type = 'file',
  name,
  url,
  onRemove,
  onPreview,
  context = 'default',
  variant = 'chip',
  size = 'md',
}) {
  const isImage = type === 'image';

  // Context-specific styles
  const contextStyles = {
    // MainInput: on bg-2, needs bg-1, rounded-[16px] to match input
    input: {
      bg: 'bg-bg-panel',
      hover: 'hover:bg-bg-page',
      imageBg: 'bg-bg-page',
      radius: 'rounded-[8px]',
    },
    // ChatBubble user: on bg-message-user, needs contrast
    user: {
      bg: 'bg-bg-active',
      hover: 'hover:bg-bg-active',
      imageBg: 'bg-bg-active',
      radius: 'rounded-[8px]',
    },
    // ChatBubble assistant: on bg-message-ai, needs contrast
    assistant: {
      bg: 'bg-bg-hover',
      hover: 'hover:bg-bg-panel',
      imageBg: 'bg-bg-panel',
      radius: 'rounded-[8px]',
    },
    // Feedback popover: on bg-card, needs contrast
    feedback: {
      bg: 'bg-bg-panel',
      hover: 'hover:bg-bg-page',
      imageBg: 'bg-bg-page',
      radius: 'rounded-[4px]',
    },
    // Fallback
    default: {
      bg: 'bg-bg-panel',
      hover: 'hover:bg-bg-hover',
      imageBg: 'bg-bg-hover',
      radius: 'rounded-[8px]',
    },
  };

  const styles = contextStyles[context] || contextStyles.default;

  // Pill variant - compact image-only display
  if (variant === 'pill' && isImage && url) {
    const pillSizes = {
      sm: 'h-8 w-[56px]',
      md: 'h-10 w-[72px]',
    };

    return (
      <div
        className={clsx(
          'relative overflow-hidden border border-border-default flex-shrink-0',
          'transition-colors duration-200',
          styles.bg,
          styles.radius,
          pillSizes[size],
          onPreview && `cursor-pointer ${styles.hover}`
        )}
        onClick={onPreview}
      >
        <img src={url} alt={name} className="w-full h-full object-cover" />
        {onRemove && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute top-spacing-1 right-spacing-1"
          >
            <button
              aria-label={`Remove ${name}`}
              className="bg-error text-tertiary rounded-full w-4 h-4 flex items-center justify-center hover:bg-error/80 transition-colors"
            >
              <CrossIcon className="size-2" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Default chip variant
  return (
    <div
      className={clsx(
        'relative flex items-center gap-spacing-2',
        'border border-border-default',
        'transition-colors duration-200 p-spacing-1 px-spacing-2',
        styles.bg,
        styles.radius,
        onPreview && `cursor-pointer ${styles.hover}`
      )}
      onClick={onPreview}
    >
      {isImage && url ? (
        <div className={clsx('rounded overflow-hidden flex-shrink-0 size-8', styles.imageBg)}>
          <img
            src={url}
            alt={name}
            className="size-full object-contain"
          />
        </div>
      ) : (
        <PaperClipIcon className="text-secondary flex-shrink-0 size-4" />
      )}

      <div className="flex-1 min-w-0">
        <p className="text-primary truncate text-dls-small">
          {name}
        </p>
      </div>

      {onRemove && (
        <div onClick={(e) => e.stopPropagation()}>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onRemove}
            aria-label={`Remove ${name}`}
          >
            <CrossIcon />
          </IconButton>
        </div>
      )}
    </div>
  );
}

AttachmentChip.propTypes = {
  type: PropTypes.oneOf(['image', 'file']),
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
  onRemove: PropTypes.func,
  onPreview: PropTypes.func,
  context: PropTypes.oneOf(['input', 'user', 'assistant', 'feedback', 'default']),
  variant: PropTypes.oneOf(['chip', 'pill']),
  size: PropTypes.oneOf(['sm', 'md']),
};
